import { message } from 'antd';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveBackendMessage } from '../Utils/apiMessages';

export const useErrorHandler = () => {
  const { t } = useTranslation();

  /**
   * Process field-level validation errors and display them inline in forms
   * @param {Object} errorsObject - Object with field names as keys and error arrays as values
   * @param {Object} form - Ant Design form instance (optional)
   * @returns {boolean} - True if errors were handled
   */
  const handleFieldErrors = useCallback((errorsObject, form) => {
    if (!errorsObject || typeof errorsObject !== 'object') return false;

    const fieldErrors = Object.entries(errorsObject).map(([fieldName, errorArray]) => {
      // Process all errors for the field (may be multiple)
      const errorMessages = errorArray.map(error =>
        resolveBackendMessage(t, {
          message_code: error.message_code,
          message: error.message,
          message_data: error.message_data
        })
      );

      return {
        name: fieldName,
        errors: errorMessages
      };
    });

    if (form) {
      // Set inline validation errors on the form
      // For nested keys like 'filters.knowledge_base', convert to array path
      const formFields = fieldErrors.map(({ name, errors }) => ({
        name: name.includes('.') ? name.split('.') : name,
        errors
      }));

      try {
        form.setFields(formFields);
      } catch (error) {
        // If setFields fails (e.g., field doesn't exist), show as toast instead
        console.warn('Could not set form fields, falling back to toast:', error);
      }

      // Also show toast messages for fields that don't exist in the form
      // This ensures users always see the error even if the field isn't in the form
      fieldErrors.forEach(({ name, errors }) => {
        const readableName = name
          .split('.')
          .map(part => part.replace(/_/g, ' '))
          .map(part => part.charAt(0).toUpperCase() + part.slice(1))
          .join(' > ');

        // Show toast notification with formatted field name
        message.error(`${readableName}: ${errors.join(', ')}`);
      });
    } else {
      // Fallback to toast notifications if no form instance provided
      // Format nested keys for readability (e.g., 'filters.knowledge_base' -> 'Filters > Knowledge Base')
      fieldErrors.forEach(({ name, errors }) => {
        const readableName = name
          .split('.')
          .map(part => part.replace(/_/g, ' '))
          .map(part => part.charAt(0).toUpperCase() + part.slice(1))
          .join(' > ');
        message.error(`${readableName}: ${errors.join(', ')}`);
      });
    }

    return true;
  }, [t]);

  const handleError = useCallback((error, form = null) => {
    // Log the error to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('Error details:', error);
    }

    const responseData = error.response?.data;

    // Handle API errors with structured payload
    if (responseData) {
      const {
        errors,
        message: errorMessage,
        message_code: messageCode,
        message_data: messageData,
        metadata,
      } = responseData;

      // NEW FORMAT: Field-level validation errors (object with field keys)
      if (errors && typeof errors === 'object' && !Array.isArray(errors)) {
        const handled = handleFieldErrors(errors, form);
        if (handled) {
          // Also show general error message as toast if available
          if (messageCode || errorMessage) {
            const generalMessage = messageCode
              ? resolveBackendMessage(t, { message_code: messageCode, message: errorMessage, message_data: messageData || metadata })
              : errorMessage;

            if (generalMessage) {
              message.error(generalMessage);
            }
          }
          return;
        }
      }

      // OLD FORMAT: Field-level validation errors (array with field/description)
      if (Array.isArray(errors) && errors.length) {
        errors.forEach(({ field, description }) => {
          message.error(
            t('field_error', {
              field,
              description,
              defaultValue: `${field}: ${description}`,
            }),
          );
        });
        return;
      }

      // Prefer localized message_code + dynamic parameters when available
      if (messageCode) {
        const resolved = resolveBackendMessage(t, {
          message_code: messageCode,
          message: errorMessage,
          message_data: messageData || metadata,
        });

        if (resolved) {
          message.error(resolved);
          return;
        }
      }

      // Fallback to raw backend message
      if (errorMessage) {
        message.error(errorMessage);
        return;
      }
    }

    // Handle network errors
    if (error.message === 'Network Error') {
      message.error(
        t('network_error_message', {
          defaultValue: 'Network error. Please check your connection and try again.',
        }),
      );
      return;
    }

    // Handle unexpected errors
    if (!responseData) {
      message.error(
        t('unexpected_error_occurred', {
          defaultValue: 'An unexpected error occurred',
        }),
      );
    }
  }, [t, handleFieldErrors]);

  return { handleError, handleErrorForm: handleError };
};
